import { Component, ViewChild, OnDestroy, OnInit, Output, EventEmitter } from '@angular/core';
import { NgForm } from '@angular/forms';
import { HttpRepositoryService } from '../../core/http-repository.service';
import { User } from '../user/user-model';
import { Company } from '../../companies/company/company.model';
import { CompanyEdit } from './company-edit.model';
import { Role } from './role.model';
import { RoleEdit } from './role-edit.model';
import { AntiForgeryTokenService } from '../../auth/anti-forgery-token.service';
import { SubSink } from 'subsink';
import { UserProfileService } from '../../user-profile/user-profile.service';

declare var $: any;
declare var Toast: any;

@Component({
  selector: 'user-edit',
  templateUrl: './user-edit.component.html',
  styleUrls: ['./user-edit.component.css']
})

export class UserEditComponent implements OnInit, OnDestroy {
  private user: User = new User();
  private isNew: boolean = true;
  private modal: any;
  private isUploaded: boolean = false;
  private dispUploadErr: any;
  private companies: Company[] = [];
  private editCompanies: CompanyEdit[] = []
  private roles: Role[] = [];
  private editRoles: RoleEdit[] = [];
  private subs = new SubSink();

  @ViewChild('editForm', { static: false }) editForm: NgForm;

  @Output()
  private saved: EventEmitter<User> = new EventEmitter<User>();

  constructor(private repository: HttpRepositoryService, private antiForgery: AntiForgeryTokenService) { }

  ngOnInit() {
    this.modal = $("#userForm");
    this.dispUploadErr = $('#uploadErr');

    this.subs.sink = this.repository.get<Company[]>('api/Admin/Companies').subscribe(resp => {
      this.companies = resp.data;
    });

    this.subs.sink = this.repository.get<Role[]>('api/Admin/Roles').subscribe(resp => {
      this.roles = resp.data;
    });
  }

  public show(user: User, isNew: boolean) {
    this.editForm.reset();
    this.user = user;
    this.isNew = isNew;

    if (this.user.image) {
      this.isUploaded = true;
    }

    let companies: CompanyEdit[] = [];
    let roles: RoleEdit[] = [];

    for (let i = 0; i < this.companies.length; i++) {
      let edit = new CompanyEdit();
      edit.id = this.companies[i].id;
      edit.code = this.companies[i].code;
      edit.name = this.companies[i].name;

      companies.push(edit);
    }

    for (let i = 0; i < this.roles.length; i++) {
      let role = new RoleEdit();
      role.id = this.roles[i].id;
      role.name = this.roles[i].name;

      roles.push(role);
    }

    if (!this.isNew) {
      for (let i = 0; i < companies.length; i++) {
        let company = this.user.companyIds.find(id => id === companies[i].id);

        if (company) {
          companies[i].isChecked = true;
        }
      }

      for (let i = 0; i < roles.length; i++) {
        let role = this.user.roleIds.find(id => id === roles[i].id);

        if (role) {
          roles[i].isChecked = true;
        }
      }
    }

    this.editCompanies = companies;
    this.editRoles = roles;

    this.modal.modal({
      show: true,
      backdrop: 'static',
      keyboard: false
    });
  }

  private upload(event: any) {
    let that = this;
    const target: DataTransfer = <DataTransfer>(event.target);
    const reader = new FileReader();

    let file = target.files[0];

    if (file) {
      let fileType = /image.*/;

      if (file.type.match(fileType)) {
        reader.onload = function (e: any) {
          that.user.image = reader.result.toString();
          that.isUploaded = true;

          if (that.dispUploadErr.children().length > 0)
            that.dispUploadErr.empty();
        }

        reader.readAsDataURL(file);
      } else {
        if (this.isUploaded)
          this.isUploaded = false;

        this.dispUploadErr.html('<div class="alert-info w-100 text-center">File is not a valid image</div>');
      }
    }
  }

  private reset() {
    this.isUploaded = false;
    this.dispUploadErr.empty();
    this.user.image = null;
  }

  private save() {
    if (this.editForm.valid) {
      this.user.companyIds = [];
      this.user.roleIds = [];

      for (let i = 0; i < this.editCompanies.length; i++) {
        if (this.editCompanies[i].isChecked) {
          this.user.companyIds.push(this.editCompanies[i].id);
        }
      }

      for (let i = 0; i < this.editRoles.length; i++) {
        if (this.editRoles[i].isChecked)
          this.user.roleIds.push(this.editRoles[i].id);
      }

      if (this.isNew)
        this.insert();
      else
        this.update();

    } else {
      Object.keys(this.editForm.controls)
        .forEach(fieldName => {
          this.editForm.controls[fieldName].markAsDirty();
        });
      $('#firstName').focus();
    }
  }

  private confirmPass(e: any) {
    this.editForm.controls['confirmPassword'].setErrors({ 'passwordDoNotMatch': true });

    if (this.editForm.controls['password'].value === e.target.value) {
      this.editForm.controls['confirmPassword'].setErrors(null);
    }
  }

  private insert() {
    this.subs.sink = this.antiForgery.getAFToken().subscribe(afResp => {
      if (afResp.hasErrors) {
        Toast.Show(afResp.error, 'Register user', 'error');
      } else {
        this.subs.sink = this.repository.post<User>('api/Admin/Register', this.user, afResp.data).subscribe(resp => {
          if (resp.hasErrors) {
            Toast.Show(resp.error, 'Register user', 'error');
          } else {
            Toast.Show('User registered successfully.', 'Register user', 'success');
          }

          this.onSaveCompleted(resp.data);
        }); 
      }
    });
  }

  private update() {
    this.subs.sink = this.antiForgery.getAFToken().subscribe(afResp => {
      if (afResp.hasErrors) {
        Toast.Show(afResp.error, 'Update user', 'error');
      } else {
        this.subs.sink = this.repository.put<User>('api/Admin/Edit', this.user, afResp.data).subscribe(resp => {
          if (resp.hasErrors) {
            Toast.Show(resp.error, 'Update user', 'error');
          } else {
            Toast.Show('User updated successfully.', 'Update user', 'success');
          }

          this.onSaveCompleted(resp.data);
        }); 
      }
    });
  }

  private onSaveCompleted(data: User) {
    this.close();
    this.saved.emit(data);
  }

  private close() {
    this.modal.modal('hide');
    this.editForm.reset();
    this.reset();
    this.user = new User();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }
}
